import { Form } from 'factorial-form'
import { isValidIBAN } from 'ibantools'
import { computed, makeObservable } from 'mobx'

import { isValidEmail } from 'modules/core/lib/email'

const countryRegex: Record<string, RegExp> = {
  at: /^U\d{8}$|^ATU\d{8}$/,
  be: /^BE[0-1]\d{9}$|^[0-1]\d{9}$/,
  bg: /^(BG\d{9}|BG\d{10}|\d{9}|\d{10})$/,
  cy: /^(CY\d{8}[a-zA-Z]|\d{8}[a-zA-Z])$/,
  cz: /^(CZ\d{8,10}|\d{8,10})$/,
  de: /^DE\d{9}|^\d{9}$/,
  dk: /^DK\d{8}|^\d{8}$/,
  ee: /^EE\d{9}|^\d{9}$/,
  es: /^(ES)?[0-9A-Z]\d{7}[0-9A-Z]$/,
  fi: /^FI\d{8}|^\d{8}$/,
  fr: /^\d{9}$|^\d{14}$|^(FR)?[0-9A-Za-z]{2}\d{9}$/,
  gb: /^(GB)?(\d{9}(\d{3})?|[A-Z]{2}\d{3}|\d{10})$/,
  gr: /^(EL)?\d{9}$/,
  hr: /^(HR)?\d{11}$/,
  hu: /^(HU)?\d{8}$/,
  ie: /^(IE)?([0-9A-Z\*\+]{7}[A-Z]{1,2}$)/,
  it: /^(IT)?\d{11}$/,
  lt: /^(LT)?(\d{9}|\d{12})$/,
  lu: /^(LU)?\d{8}$/,
  lv: /^(LV)?\d{11}$/,
  mt: /^(MT)?\d{8}$/,
  nl: /^\d{9}B\d{2}$|^NL\d{9}B\d{2}$/,
  pl: /^(PL)?\d{10}$/,
  pt: /^(PT)?\d{9}$/,
  ro: /^(RO)?\d{2,10}/,
  se: /^(SE)?\d{12}/,
  si: /^(SI)?\d{8}/,
  sk: /^(SK)?\d{10}/,
  mx: /^[A-ZÑ&]{3,4}[0-9]{2}(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1])(?:[A-Z\d]{3})/,
  br: /^[0-9]{2}[\.]?[0-9]{3}[\.]?[0-9]{3}[\/]?[0-9]{4}[-]?[0-9]{2}$/,
  us: /^(?![01][789]|2[89]|[46]9|7[089]|89|9[67]|012345678|123456789|000000000|111111111|222222222|333333333|444444444|555555555|666666666|777777777|888888888|999999999|)\d\d\d{7}$/,
}

export type ChargebeeSubscriptionFormFirstStep = {
  country: string
  mx_tax_system: string | null
  vat_number: string | null
  company: string | null
  address_line: string | null
  city: string | null
  zip: string | null
  state: string | null
  email: string | null | undefined
}

export type ChargebeeSubscriptionFormSecondStep = {
  payment_method: string
  card_number: string | null
  card_expiry: string | null
  card_cvv: string | null
  iban: string | null
  vat_has_been_validated: boolean | null
  vat_validation: boolean | null
}

export type ChargebeeSubscriptionFormValues = ChargebeeSubscriptionFormFirstStep &
  ChargebeeSubscriptionFormSecondStep

export class ChargebeeSubscriptionForm extends Form {
  constructor(values: ChargebeeSubscriptionFormValues) {
    super(values, {
      country: 'string',
      mx_tax_system: 'string',
      vat_number: 'string',
      company: 'string',
      address_line: 'string',
      city: 'string',
      zip: 'string',
      state: 'string',
      email: 'string',
      payment_method: 'string',
      card_number: 'string',
      card_expiry: 'string',
      card_cvv: 'string',
      iban: 'string',
      vat_has_been_validated: 'boolean',
      vat_validation: 'boolean',
    })
    makeObservable(this, {
      visibleFields: computed,
      isBillingInfoValid: computed,
      isPaymentDetailsValid: computed,
      isValidEmail: computed,
      isValidTaxId: computed,
      isValidIban: computed,
      isValidCardNumber: computed,
      isValidCardExpiry: computed,
      isValidCardCVV: computed,
    })
  }

  get cleanTaxId(): string {
    return this.get('vat_number')
      .value.toUpperCase()
      .replace(/[^A-Z0-9]+/g, '')
  }

  get isValidTaxId(): boolean {
    if (this.get('vat_has_been_validated').value) {
      return this.get('vat_validation').value
    }
    const country = this.get('country').value
    const regexForCountry = countryRegex[country]
    if (regexForCountry) {
      const regEx = new RegExp(regexForCountry)
      const vat_number = this.cleanTaxId
      return regEx.test(vat_number)
    } else {
      return true
    }
  }

  get isValidEmail(): boolean {
    return isValidEmail(this.get('email').value)
  }

  get isValidIban(): boolean {
    return isValidIBAN(this.get('iban').value)
  }

  get isValidCardNumber(): boolean {
    const cardNumber = this.get('card_number').value.replace(/[^\d]/g, '')
    const cardNumberRegex = /^[0-9]{16}$/
    return cardNumberRegex.test(cardNumber)
  }

  get isValidCardExpiry(): boolean {
    return this.getErrorCardExpiry === null
  }

  get getErrorCardExpiry(): string | null {
    const cardExpiry = this.get('card_expiry').value
    const cardExpiryRegex = /^(0[1-9]|1[0-2]) \/ ([0-9]{2})$/
    const isValidFormat = cardExpiryRegex.test(cardExpiry)
    if (!isValidFormat) {
      return 'invalid'
    }
    const month = Number(cardExpiry.split(' / ')[0])
    if (month < 1 || month > 12) {
      return 'invalid'
    }
    const year = Number(cardExpiry.split(' / ')[1])
    const currentYear = new Date().getFullYear() % 100
    const currentMonth = new Date().getMonth() + 1
    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return 'expired'
    }
    return null
  }

  get isValidCardCVV(): boolean {
    const cardCVV = this.get('card_cvv').value
    const cardCVVRegex = /^[0-9]{3}$/
    return cardCVVRegex.test(cardCVV)
  }

  get isBillingInfoValid(): boolean {
    if (
      this.get('country').value === '' ||
      this.get('company').value === '' ||
      this.get('address_line').value === '' ||
      this.get('email').value === '' ||
      !this.isValidEmail
    ) {
      return false
    }

    switch (this.get('country').value) {
      case 'es':
      case 'br':
        return (
          this.get('zip').value !== '' && this.get('vat_number').value !== '' && this.isValidTaxId
        )
      case 'mx':
        if (this.get('mx_tax_system').value === '616') {
          return this.get('city').value !== '' && this.get('zip').value !== ''
        }
        return (
          this.get('mx_tax_system').value !== '' &&
          this.get('city').value !== '' &&
          this.get('zip').value !== '' &&
          this.get('vat_number').value !== '' &&
          this.get('state').value !== '' &&
          this.isValidTaxId
        )
      case 'us':
        return (
          this.get('state').value !== '' && this.get('vat_number').value !== '' && this.isValidTaxId
        )
      case 'cu':
      case 'kp':
      case 'sy':
      case 'ir':
      case 'ru':
      case 'ua':
        return false
    }
    return this.get('vat_number').value !== '' && this.isValidTaxId
  }

  get isPaymentDetailsValid(): boolean {
    switch (this.get('payment_method').value) {
      case 'card':
        return (
          this.get('card_number').value !== '' &&
          this.get('card_expiry').value !== '' &&
          this.get('card_cvv').value !== '' &&
          this.isValidCardNumber &&
          this.isValidCardExpiry &&
          this.isValidCardCVV
        )
      case 'bank-transfer':
        return this.get('iban').value !== '' && this.isValidIban
      case 'pix':
      case 'boleto':
        return true
    }
    return false
  }

  get visibleFields() {
    if (!this.get('country').value) return []
    const defaultFields = ['company', 'address_line', 'zip', 'email']
    switch (this.get('country').value) {
      case 'mx':
        if (!this.get('mx_tax_system').value || this.get('mx_tax_system').value === '616') {
          return [...defaultFields, 'mx_tax_system', 'city', 'state']
        }
        return [...defaultFields, 'mx_tax_system', 'vat_number', 'city', 'state']
      case 'br':
        return [...defaultFields, 'vat_number', 'state']
      case 'us':
        return [...defaultFields, 'vat_number', 'state']
    }
    return [...defaultFields, 'vat_number', 'state']
  }

  get firstStepValues() {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    return this.visibleFields.reduce(
      (acc, fieldName) => ({
        ...acc,
        [fieldName]: this.get(fieldName).value,
      }),
      {}
    ) as ChargebeeSubscriptionFormFirstStep
  }
}
